/**
 * `<SkyLayer>`: mounts a Godot sky as the scene's background and IBL, and shows its radiance to every
 * lit material and both fogs before each render of the scene, and black after it (`skyRadiance.ts`),
 * while its Environment is on the scene (`sceneEnvironment.tsx`).
 * Godot's sky shader draws the discs of the first four DirectionalLights that are not Light Only as
 * `LIGHT0..3`. The lights are read from the rendered three.js scene, where an instanced or GLB light
 * has its real world transform. The world's exposure and `background_energy_multiplier` scale the
 * sky as Godot does: the background and each disc once, and the ambient and the reflection twice,
 * since Godot bakes the radiance at both and a surface reads it at both again
 * (`render_forward_clustered.cpp:1993`, `render_scene_data_rd.cpp:169`, `:241-246`, `sky.cpp:1086`).
 */
import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { visibleInTree } from '../visibleInTree';
import { useFrame, useThree } from '@react-three/fiber';
import { NO_SKY_LIGHTS, noteSkyLights, skyLightEnergy, skyLightSize } from './skyLight';
import { skyEuler } from './skyRotation';
import { hideShownSky } from './skyRadiance';
import { holdSkyBakes, radianceUpdateOf, skyUpdatePlan } from './skyBakes';
import { materialReadsLights } from '../../resources/sky/readsLights';
import { stepSkyRadiance } from '../../godot/rendering/environment/skyRadianceUpdate';
import { ENVIRONMENT_SWITCH } from './environmentSwitch';
import { observeSceneCamera, observeSceneRenderEnd } from '../sceneRenderCamera';
import { useExposureFactor } from '../environment/worldExposure';
import { drawnExposureOf } from '../exposure/sceneExposure';
import { noteEnvironmentChange, registerEnvironmentPart, useEnvironmentKey, } from '../environment/sceneEnvironment';
import { debandSky } from './skyDebanding';
import { showSkyBackground } from './skyBackground';
import { isLiveTarget, liveTargetDrawCount } from '../liveTargets';
import { isDebanded } from '../../resources/sky/debanding';
/** Godot's sky shader has four light slots. The other lights do not reach it. */
const LIGHT_SLOTS = 4;
export function SkyLayer({ sky, energyMultiplier = 1, contribution = 1, reflects = true, asBackground = true, rotation, panorama, fogScattersSun = false, }) {
    const gl = useThree((state) => state.gl);
    const scene = useThree((state) => state.scene);
    const exposure = useExposureFactor();
    const environment = useEnvironmentKey();
    const lightsKey = useSkyLightsKey(scene);
    const contextRestores = useContextRestores(gl);
    const { x: rotationX, y: rotationY, z: rotationZ } = rotation;
    // Refs, not effect dependencies: a new share or reflection source needs no new bake.
    const contributionRef = useRef(contribution);
    contributionRef.current = contribution;
    const reflectsRef = useRef(reflects);
    reflectsRef.current = reflects;
    const isPanoramaDrawn = useIsPanoramaDrawn(panorama);
    // Written only by the effect below, while it holds bakes.
    const bakeWantedRef = useRef(null);
    useFrame(({ invalidate }) => {
        if (bakeWantedRef.current?.())
            invalidate();
    });
    useEffect(() => {
        // No frame has read the lights yet. A bake now would be replaced before it
        // is seen, and each bake costs a six-face cube render and the layer passes.
        if (lightsKey === null)
            return undefined;
        // A bake samples the panorama once, so it waits for the pass to draw a live target.
        if (!isPanoramaDrawn)
            return undefined;
        const lights = directionalLights(scene);
        const skyRotation = skyEuler({ x: rotationX, y: rotationY, z: rotationZ });
        const input = { sky, lights, panorama, asBackground, energyMultiplier, rotation: skyRotation };
        const { bakes, release } = holdSkyBakes(gl, input, lightsKey, contextRestores);
        const update = radianceUpdateOf(sky);
        const plan = skyUpdatePlan(sky);
        const readsLights = (materialReadsLights(sky.material) || fogScattersSun) && lights.length > 0;
        const lightData = readsLights ? lightsKey : NO_SKY_LIGHTS;
        // The exposure scales the light data, and so dirties the sky, unless every energy is 0.
        const exposureScalesLights = readsLights && lights.some((light) => light.energy !== 0);
        const first = bakes.bakeNow(update.baked?.exposure ?? exposure);
        if (!first) {
            release();
            return undefined;
        }
        let radiance = first;
        bakeWantedRef.current = bakeWantedOf(bakes, () => noteEnvironmentChange(scene, environment));
        // A render draws the background live at its own exposure. A surface reads the radiance at the
        // brightness that baked it, times its own exposure and its `background_energy_multiplier`
        // (`render_forward_clustered.cpp:2045`, `render_scene_data_rd.cpp:169`,
        // `scene_forward_clustered.glsl:1732`, `:1756`). A fog reads it without the multiplier (`:1121`).
        const brightnessAt = (drawn) => energyMultiplier * drawn;
        const bakedBrightness = (drawn) => update.baked?.brightness ?? brightnessAt(drawn);
        const showAt = (drawn) => {
            radiance = bakes.at(update.baked?.exposure ?? drawn) ?? radiance;
            scene.environmentIntensity = bakedBrightness(drawn) * brightnessAt(drawn);
        };
        // Each render shows the sky at the exposure it draws at, a staged draw too (`drawAtExposure`).
        const showForRender = () => {
            const drawn = drawnExposureOf(scene);
            const isDirtied = noteSkyLights(gl, lightData, exposureScalesLights ? drawn : 1);
            stepSkyRadiance(update, plan, { exposure: drawn, brightness: brightnessAt(drawn) }, isDirtied);
            showAt(drawn);
            // Only a background reads the bake at the drawn exposure: for a sky that paints none, the
            // read would bake that exposure to no use.
            if (asBackground) {
                (bakes.at(drawn) ?? radiance).background?.drawIn(scene, brightnessAt(drawn), skyRotation);
            }
            radiance.radiance.show(contributionRef.current, reflectsRef.current, bakedBrightness(drawn));
        };
        const stopRegistering = registerEnvironmentPart(scene, environment, () => {
            const previousEnvironment = scene.environment;
            const previousIntensity = scene.environmentIntensity;
            scene.environment = ENVIRONMENT_SWITCH;
            // Observed, not assigned: the visibility cull and the shadow fitter share the scene's hook.
            const stopShowingRadiance = observeSceneCamera(scene, showForRender);
            const stopHidingRadiance = observeSceneRenderEnd(scene, hideShownSky);
            const stopShowingBackground = asBackground ? showSkyBackground(scene) : undefined;
            showAt(exposure);
            const stopDebanding = asBackground && isDebanded(sky.material) ? debandSky(gl, scene) : undefined;
            return () => {
                stopShowingRadiance();
                stopHidingRadiance();
                stopShowingBackground?.();
                stopDebanding?.();
                scene.environment = previousEnvironment;
                scene.environmentIntensity = previousIntensity;
            };
        });
        return () => {
            stopRegistering();
            bakeWantedRef.current = null;
            release();
        };
    }, [
        gl,
        scene,
        environment,
        sky,
        fogScattersSun,
        energyMultiplier,
        exposure,
        panorama,
        isPanoramaDrawn,
        asBackground,
        lightsKey,
        contextRestores,
        rotationX,
        rotationY,
        rotationZ,
    ]);
    return null;
}
/**
 * Bakes each exposure a render of `bakes` asked for, after it redraws the backgrounds for a new draw
 * of a live panorama, and answers whether a frame should draw. A bake that lands, whichever layer
 * baked it, runs `noteBake`: a reflection probe captured the bake it replaces, and captures again
 * only for a change of its Environment (`sceneProbes.ts`).
 */
function bakeWantedOf(bakes, noteBake) {
    let seenCount = bakes.bakeCount;
    return () => {
        bakes.followPanorama();
        const isBaked = bakes.bakeWanted();
        if (bakes.bakeCount === seenCount)
            return isBaked;
        seenCount = bakes.bakeCount;
        noteBake();
        return true;
    };
}
/**
 * Whether `panorama` holds a picture a bake may sample: false for a live target until its pass first
 * draws it, read each frame until then, and true for any other texture or none.
 */
function useIsPanoramaDrawn(panorama) {
    const isLive = !!panorama && isLiveTarget(panorama);
    const [drawnPanorama, setDrawnPanorama] = useState(null);
    const isDrawn = !isLive || drawnPanorama === panorama;
    useFrame(() => {
        if (!isDrawn && panorama && liveTargetDrawCount(panorama) > 0)
            setDrawnPanorama(panorama);
    });
    return isDrawn;
}
/**
 * Counts the contexts `gl` restores. A lost context loses every texture the bake drew, and the bake
 * keeps nothing on the CPU to upload again, so the sky bakes again on each.
 */
function useContextRestores(gl) {
    const [restores, setRestores] = useState(0);
    useEffect(() => {
        const restored = () => setRestores((count) => count + 1);
        gl.domElement.addEventListener('webglcontextrestored', restored);
        return () => gl.domElement.removeEventListener('webglcontextrestored', restored);
    }, [gl]);
    return restores;
}
/**
 * Changes whenever the lights the sky draws change, read every frame, as Godot re-renders the sky
 * on a change of light count, direction, energy, colour or size (`sky.cpp:1104-1142`). A sub-scene
 * or GLB brings a light late, and the preview sun unmounts for the scene's own (ADR-0025). Null
 * until the first frame.
 */
function useSkyLightsKey(scene) {
    const [key, setKey] = useState(null);
    const latest = useRef(null);
    useFrame(() => {
        const next = skyLightsKey(directionalLights(scene));
        if (next === latest.current)
            return;
        latest.current = next;
        setKey(next);
    });
    return key;
}
function skyLightsKey(lights) {
    return lights
        .map(({ direction: d, color: c, energy, size }) => [d.x, d.y, d.z, c.r, c.g, c.b, energy, size].join(','))
        .join(';');
}
/**
 * The scene's directional lights, as Godot's sky shader wants them: the
 * direction towards the light (where its disc appears), its colour, and its
 * energy. three's `DirectionalLight` shines from its own world position toward
 * its target's, so the sky direction is that vector reversed.
 */
function directionalLights(scene) {
    const lights = [];
    const from = new THREE.Vector3();
    const to = new THREE.Vector3();
    scene.traverse((object) => {
        if (lights.length >= LIGHT_SLOTS)
            return;
        if (!object.isDirectionalLight)
            return;
        // `traverse` descends into hidden subtrees, which three skips when lighting.
        // Godot's sky sees only the render list, so a hidden light draws no disc.
        if (!visibleInTree(object))
            return;
        const light = object;
        const energy = skyLightEnergy(light);
        if (energy === null)
            return;
        light.getWorldPosition(from);
        light.target.getWorldPosition(to);
        const direction = from.clone().sub(to);
        if (direction.lengthSq() === 0)
            direction.set(0, 1, 0);
        lights.push({
            direction: direction.normalize(),
            color: light.color.clone(),
            // `sky.cpp` sets `sky_light_data.energy` from LIGHT_PARAM_ENERGY with no PI,
            // unlike the scene shader.
            energy,
            size: skyLightSize(light),
        });
    });
    return lights;
}
//# sourceMappingURL=SkyLayer.js.map